import { datadogRum } from '@datadog/browser-rum';

/**
 * Log levels for the application
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

/**
 * Simple logger that integrates with Datadog RUM
 */
export class Logger {
  private context: string;
  private static minLevel: LogLevel = (
    import.meta.env.VITE_APP_NODE_ENV === 'prod' || 
    process.env.NODE_ENV === 'production'
  ) ? LogLevel.INFO : LogLevel.DEBUG;
  private static hasLoggedEnvInfo: boolean = false;

  /**
   * Create a new logger instance
   * @param context The context (usually component or service name)
   */
  constructor(context: string) {
    this.context = context;
    
    // Log environment info for the first logger instance only
    if (!Logger.hasLoggedEnvInfo) {
      Logger.hasLoggedEnvInfo = true;
      const isDev = import.meta.env.VITE_APP_NODE_ENV !== 'prod' && process.env.NODE_ENV !== 'production';
      console.log(`Logger initialized - Environment: ${isDev ? 'development' : 'production'} (VITE_APP_NODE_ENV: ${import.meta.env.VITE_APP_NODE_ENV}, NODE_ENV: ${process.env.NODE_ENV}, minLevel: ${Logger.minLevel})`);
    }
  }

  /**
   * Log a debug message
   * @param message The message to log
   * @param optionalParams Additional parameters
   */
  public debug(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.DEBUG, message, optionalParams);
  }

  /**
   * Log an info message
   * @param message The message to log
   * @param optionalParams Additional parameters
   */
  public info(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.INFO, message, optionalParams);
  }

  /**
   * Log a warning message
   * @param message The message to log
   * @param optionalParams Additional parameters
   */
  public warn(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.WARN, message, optionalParams);
  }

  /**
   * Log an error message
   * @param message The message to log
   * @param optionalParams Additional parameters
   */
  public error(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.ERROR, message, optionalParams);
  }

  /**
   * Log a message with the specified level
   * @param level The log level
   * @param message The message to log
   * @param optionalParams Additional parameters
   */
  private log(level: LogLevel, message: string, optionalParams: any[]): void {
    if (level < Logger.minLevel) return;

    const formattedMessage = `[${this.context}] ${message}`;

    // Always log to console in development or for warnings/errors
    const isDevelopment = import.meta.env.VITE_APP_NODE_ENV !== 'prod' && process.env.NODE_ENV !== 'production';
    if (isDevelopment || level >= LogLevel.WARN) {
      this.logToConsole(level, formattedMessage, optionalParams);
    }

    // Send to Datadog RUM
    this.logToDatadog(level, formattedMessage, optionalParams);
  }

  /**
   * Log to the browser console
   * @param level Log level
   * @param message Formatted message
   * @param params Additional parameters
   */
  private logToConsole(level: LogLevel, message: string, params: any[]): void {
    const isDevelopment = import.meta.env.VITE_APP_NODE_ENV !== 'prod' && process.env.NODE_ENV !== 'production';
    
    switch (level) {
      case LogLevel.DEBUG:
        // Use console.log in development to ensure visibility (console.debug is often hidden)
        if (isDevelopment) {
          console.log(message, ...params);
        } else {
          console.debug(message, ...params);
        }
        break;
      case LogLevel.INFO:
        // Use console.log in development to ensure visibility (console.info is often hidden)
        if (isDevelopment) {
          console.log(message, ...params);
        } else {
          console.info(message, ...params);
        }
        break;
      case LogLevel.WARN:
        console.warn(message, ...params);
        break;
      case LogLevel.ERROR:
        console.error(message, ...params);
        break;
    }
  }

  /**
   * Send log to Datadog RUM
   * @param level Log level
   * @param message Formatted message
   * @param params Additional parameters
   */
  private logToDatadog(level: LogLevel, message: string, params: any[]): void {
    try {
      // Only send if Datadog RUM is initialized
      if (!datadogRum || typeof datadogRum.addError !== 'function') {
        return;
      }

      const context = {
        context: this.context,
        timestamp: new Date().toISOString(),
        params: {}
      };

      // Add parameters if present and not empty
      if (params.length > 0) {
        try {
          // Handle params with circular references
          context.params = JSON.parse(JSON.stringify(params));
        } catch (e) {
          // If JSON stringification fails, convert to strings
          context.params = params.map((p) => String(p));
        }
      }

      switch (level) {
        case LogLevel.DEBUG:
          // Debug logs as custom events in Datadog
          datadogRum.addAction('log.debug', {
            message,
            ...context
          });
          break;
        case LogLevel.INFO:
          // Info logs as custom events in Datadog
          datadogRum.addAction('log.info', {
            message,
            ...context
          });
          break;
        case LogLevel.WARN:
          // Warnings as errors with warning status
          datadogRum.addError(message, {
            ...context,
            level: 'warning'
          });
          break;
        case LogLevel.ERROR:
          // Handle Error objects specially
          const errorParam = params.find((p) => p instanceof Error);

          if (errorParam) {
            datadogRum.addError(errorParam, {
              ...context,
              level: 'error',
              message // Include the custom message
            });
          } else {
            datadogRum.addError(message, {
              ...context,
              level: 'error'
            });
          }
          break;
      }
    } catch (error) {
      // Don't let logging errors affect the application
      console.error('Failed to log to Datadog RUM', error);
    }
  }

  /**
   * Set the minimum log level
   * @param level Minimum log level
   */
  public static setMinLevel(level: LogLevel): void {
    Logger.minLevel = level;
  }
}
